import React, { useCallback, useEffect, useRef, useState } from "react";
import moment from "moment";
import Header from "../layout/Header";
import Shimmer from "src/components/common/Shimmer";
import { ChevronRight, Scale } from "lucide-react";
import {
  DATE_FORMAT_STRING,
  TIME_FORMAT_STRING,
} from "src/constants/dataTimeFormats";
import { useAppSelector } from "src/redux/hooks";
import insightsApis from "src/api/insights";
import bcaApis from "src/api/bca";
import DeviceResult from "../smart-scale/DeviceResult";

export interface BcaLogItem {
  created_at?: string;
  weight?: number | string;
  bca_sync_logs_id?: string | number;
  synced_device?: string;
  body_fat_percentage?: number | string;
  muscle_mass?: number | string;
  bmi?: number | string;
  [key: string]: unknown;
}

interface HistoryItem {
  id?: string | number;
  created_at?: string;
  timestamp?: string;
  time?: string;
  value?: number | string;
  medicine_name?: string;
  name?: string;
  dose?: string | number;
  bca_sync_logs_id?: string | number;
  synced_device?: string;
  weight?: number | string;
  body_fat_percentage?: number | string;
  muscle_mass?: number | string;
  [key: string]: unknown;
}

interface InsightHistoryProps {
  onBackClick: () => void;
  supportProgramKey?: string;
  logger?: string;
  icon?: React.ReactNode;
}

const LIMIT = 10;

const InsightHistory: React.FC<InsightHistoryProps> = ({
  onBackClick,
  supportProgramKey,
  logger = "bca",
  icon,
}) => {
  const { patient } = useAppSelector((state) => state.auth);

  const [logs, setLogs] = useState<HistoryItem[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedBcaLog, setSelectedBcaLog] = useState<HistoryItem | null>(
    null
  );
  const scrollableRef = useRef<HTMLDivElement>(null);

  const isBca = logger === "bca";

  const fetchBcaHistory = useCallback(
    async (pageNum: number, append: boolean) => {
      if (pageNum === 1) setLoading(true);
      else setLoadingMore(true);
      try {
        const res: any = await bcaApis.getBcaLogs({
          queryParams: { page: pageNum, limit: String(LIMIT) },
        });
        const code = res?.code != null ? Number(res.code) : null;
        if (code === -1 || code === 0) {
          if (!append) setLogs([]);
          return;
        }
        const list: HistoryItem[] = res?.data?.bca_logs ?? [];
        const total = res?.data?.total_records ?? 0;
        setTotalRecords(total);
        setLogs((prev) => (append ? [...prev, ...list] : list));
      } catch {
        if (!append) setLogs([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    []
  );

  const fetchLoggerHistory = useCallback(
    async (pageNum: number, append: boolean) => {
      const userId = patient?.data?.patient_id;
      if (!userId) {
        if (!append) setLogs([]);
        setLoading(false);
        return;
      }
      if (pageNum === 1) setLoading(true);
      else setLoadingMore(true);
      try {
        const res: any = await insightsApis.getHistory({
          user_id: userId,
          logger,
          page: pageNum,
          limit: LIMIT,
        });
        const code = res?.code != null ? Number(res.code) : null;
        if (code === -1 || code === 0) {
          if (!append) setLogs([]);
          return;
        }
        const list: HistoryItem[] = res?.data?.history ?? [];
        const total = res?.data?.full_count ?? res?.data?.total_records ?? 0;
        setTotalRecords(total);
        setLogs((prev) => (append ? [...prev, ...list] : list));
      } catch {
        if (!append) setLogs([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [patient?.data?.patient_id, logger]
  );

  const fetchHistory = isBca ? fetchBcaHistory : fetchLoggerHistory;

  useEffect(() => {
    setLogs([]);
    setPage(1);
    setTotalRecords(0);
    fetchHistory(1, false);
    scrollableRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, [fetchBcaHistory, fetchLoggerHistory, isBca]);

  useEffect(() => {
    if (page > 1) fetchHistory(page, true);
  }, [page, fetchBcaHistory, fetchLoggerHistory, isBca]);

  const handleLoadMore = () => {
    if (loadingMore || logs.length >= totalRecords) return;
    setPage((p) => p + 1);
  };

  const hasMore = logs.length < totalRecords;

  if (isBca && selectedBcaLog) {
    const logId = String(selectedBcaLog.bca_sync_logs_id ?? "");
    const createdAt = String(selectedBcaLog.created_at ?? "");
    return (
      <DeviceResult
        bcaLogId={logId}
        createdAt={createdAt}
        onBackClick={() => setSelectedBcaLog(null)}
      />
    );
  }

  const getTitle = (log: HistoryItem) => {
    switch (logger) {
      case "medication-logs":
        return `${
          log.medicine_name ??
          log.name ??
          (log.value != null ? String(log.value) : logger)
        } ${log.unit ?? ""}`;
      case "food":
        return `${log.meal_name} - ${log.calories}`;
      case "bca":
        return `${log.weight} kg`;
      default:
        return `${
          log.name ?? (log.value != null ? String(log.value) : logger)
        } ${log.unit ?? ""}`;
    }
  };

  const getSubtitle = (log: HistoryItem): string | React.ReactNode | null => {
    switch (logger) {
      case "medication-logs":
        return log.dose != null ? String(log.dose) : null;
      case "food":
        return log?.meal_type as string | null;
      case "bca":
        return log.body_fat_percentage != null || log.muscle_mass != null ? (
          <div className="flex gap-3 mt-1 text-xs text-gray-600">
            {log.body_fat_percentage != null && (
              <span>
                Body fat: {Number(log.body_fat_percentage).toFixed(1)}%
              </span>
            )}
            {log.muscle_mass != null && (
              <span>Muscle: {Number(log.muscle_mass).toFixed(1)} kg</span>
            )}
          </div>
        ) : null;
      default:
        return null;
    }
  };

  return (
    <div className="relative min-h-0 bg-gray-100">
      <Header
        title="Show All Data"
        titlePosition="center"
        showBackButton
        onBackClick={onBackClick}
        className="sticky top-0 left-0 right-0 z-10"
      />
      <div className="relative overflow-y-auto" ref={scrollableRef}>
        <div className="p-4">
          <p className="text-[var(--text-secondary)] text-sm mb-6">
            Showing detailed logs from health assistant action flows with user
            inputs and calculated outputs.
          </p>

          {loading && logs.length === 0 ? (
            <div className="space-y-4">
              <Shimmer width="100%" height="72px" />
              <Shimmer width="100%" height="72px" />
              <Shimmer width="100%" height="72px" />
              <Shimmer width="100%" height="72px" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-[var(--text-secondary)]">
              No logs available
            </div>
          ) : (
            <>
              <div className="space-y-4">
                {logs.map((log, index) => (
                  <LogHistoryItem
                    key={log.id ?? log.created_at ?? index}
                    icon={icon}
                    title={getTitle(log)}
                    subtitle={getSubtitle(log)}
                    timestamp={
                      log.time ?? log.timestamp ?? log.created_at ?? ""
                    }
                    onTap={() => (isBca ? setSelectedBcaLog(log) : null)}
                  />
                ))}
              </div>

              {!loading && hasMore && (
                <div className="mt-6 text-center">
                  <button
                    type="button"
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    className="py-2 px-4 rounded-xl border border-gray-300 text-sm font-medium disabled:opacity-50"
                  >
                    {loadingMore ? "Loading..." : "Load more"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default InsightHistory;

const LogHistoryItem: React.FC<{
  icon?: React.ReactNode;
  title: string;
  subtitle?: string | React.ReactNode | null;
  timestamp: string;
  onTap?: () => void;
}> = ({ icon, title, subtitle, timestamp, onTap }) => {
  const dateLabel = moment(timestamp).format(DATE_FORMAT_STRING);
  const timeLabel = moment(timestamp).format(TIME_FORMAT_STRING);

  return (
    <div
      className="w-full p-4 shadow-sm border-0 bg-white rounded-lg overflow-hidden"
      onClick={onTap}
    >
      <div className="flex items-center gap-4">
        {icon && (
          <div className="flex items-center justify-center w-10 h-10 rounded-lg text-[var(--app-primary)] bg-[var(--icon-bg-primary)] flex-shrink-0">
            {icon}
          </div>
        )}
        <div className="w-full flex flex-col gap-1 min-w-0">
          <div className="w-full flex items-start justify-between gap-2">
            <span className="text-sm font-medium text-gray-800">{title}</span>
          </div>
          {subtitle ? (
            <div className="text-xs text-gray-600 -mt-1">{subtitle}</div>
          ) : null}
          {timeLabel || dateLabel ? (
            <div className="text-xs font-light text-gray-500">
              {dateLabel}
              {timeLabel ? ` • ${timeLabel}` : ""}
            </div>
          ) : null}
        </div>
        {onTap ? (
          <button
            className="p-1.5 bg-gray-100 rounded-full"
            type="button"
            onClick={onTap}
          >
            <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
          </button>
        ) : null}
      </div>
    </div>
  );
};
